import React, { useState, useEffect, useRef } from "react"
import { Button } from "../ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../../lib/utils"
import { attorneysApi, Attorney } from "../../services/attorneysApi"
import { firmsApi, Firm } from "../../services/firmsApi"

interface AssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  assignmentType: "attorney" | "firm" | null
  onAssignment: (type: "attorney" | "firm", id: string) => Promise<void>
  currentAssignment?: {
    type: "attorney" | "firm"
    id: string
    name: string
  } | null
  onRemoveAssignment?: () => Promise<void>
}

export default function AssignmentModal({
  isOpen,
  onClose,
  assignmentType,
  onAssignment,
  currentAssignment,
  onRemoveAssignment
}: AssignmentModalProps) {
  const modalContainerRef = useRef<HTMLDivElement | null>(null)
  const [selectedAttorney, setSelectedAttorney] = useState("")
  const [selectedFirm, setSelectedFirm] = useState("")
  const [attorneys, setAttorneys] = useState<Attorney[]>([])
  const [firms, setFirms] = useState<Firm[]>([])
  const [loadingAttorneys, setLoadingAttorneys] = useState(false)
  const [loadingFirms, setLoadingFirms] = useState(false)
  const [attorneyPopoverOpen, setAttorneyPopoverOpen] = useState(false)
  const [firmPopoverOpen, setFirmPopoverOpen] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  // Load attorneys and firms when modal opens
  useEffect(() => {
    if (isOpen && assignmentType) {
      if (assignmentType === "attorney" && attorneys.length === 0) {
        loadAttorneys()
      } else if (assignmentType === "firm" && firms.length === 0) {
        loadFirms()
      }
    }
  }, [isOpen, assignmentType, attorneys.length, firms.length])

  // Pre-select current assignment when modal opens
  useEffect(() => {
    if (isOpen && currentAssignment && assignmentType === currentAssignment.type) {
      if (currentAssignment.type === "attorney") {
        setSelectedAttorney(currentAssignment.id)
      } else if (currentAssignment.type === "firm") {
        setSelectedFirm(currentAssignment.id)
      }
    } else if (isOpen && (!currentAssignment || assignmentType !== currentAssignment.type)) {
      // Clear selection if no current assignment or different type
      setSelectedAttorney("")
      setSelectedFirm("")
    }
  }, [isOpen, currentAssignment, assignmentType])

  const loadAttorneys = async () => {
    setLoadingAttorneys(true)
    try {
      const response = await attorneysApi.getAttorneys(1, 100)
      setAttorneys(response.attorneys)
    } catch (error) {
      console.error("Failed to load attorneys:", error)
    } finally {
      setLoadingAttorneys(false)
    }
  }

  const loadFirms = async () => {
    setLoadingFirms(true)
    try {
      const response = await firmsApi.getFirms(1, 100)
      setFirms(response.firms)
    } catch (error) {
      console.error("Failed to load firms:", error)
    } finally {
      setLoadingFirms(false)
    }
  }

  const handleAssignment = async () => {
    // Close modal immediately
    const selectedId = assignmentType === "attorney" ? selectedAttorney : selectedFirm
    
    // Reset selections and close modal first
    setSelectedAttorney("")
    setSelectedFirm("")
    setAttorneyPopoverOpen(false)
    setFirmPopoverOpen(false)
    onClose()
    
    // Then handle the assignment in the background
    if (assignmentType === "attorney" && selectedId) {
      onAssignment("attorney", selectedId).catch(error => {
        console.error("Failed to assign to attorney:", error)
      })
    } else if (assignmentType === "firm" && selectedId) {
      onAssignment("firm", selectedId).catch(error => {
        console.error("Failed to refer to firm:", error)
      })
    }
  }

  const handleRemoveAssignment = async () => {
    if (!onRemoveAssignment) return
    
    setIsRemoving(true)
    try {
      await onRemoveAssignment()
      // Reset selections and close modal
      setSelectedAttorney("")
      setSelectedFirm("")
      setAttorneyPopoverOpen(false)
      setFirmPopoverOpen(false)
      onClose()
    } catch (error) {
      console.error("Failed to remove assignment:", error)
    } finally {
      setIsRemoving(false)
    }
  }

  const handleClose = () => {
    setSelectedAttorney("")
    setSelectedFirm("")
    setAttorneyPopoverOpen(false)
    setFirmPopoverOpen(false)
    onClose()
  }

  if (!assignmentType) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent ref={modalContainerRef} className="w-[90vw] max-w-sm mx-auto sm:max-w-md transition-none">
        <DialogHeader>
          <DialogTitle>
            {assignmentType === "attorney" ? "Assign to Attorney" : "Refer to Firm"}
          </DialogTitle>
          <DialogDescription>
            {assignmentType === "attorney" 
              ? "Select an attorney to assign this lead to" 
              : "Select a firm to refer this lead to"
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {assignmentType === "attorney" ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Select Attorney</label>
              <Popover open={attorneyPopoverOpen} onOpenChange={setAttorneyPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between text-left font-normal h-10 px-3 py-2"
                  >
                    <span className="truncate">
                      {selectedAttorney 
                        ? attorneys.find(a => a.id === selectedAttorney)?.name 
                        : "Choose an attorney..."
                      }
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  container={modalContainerRef.current}
                  className="w-[var(--radix-popover-trigger-width)] min-w-[200px] max-w-[400px] p-0" 
                  align="start"
                  side="bottom"
                  sideOffset={4}
                  onWheel={(e) => e.stopPropagation()}
                >
                  <Command className="w-full h-full">
                    <CommandInput 
                      placeholder="Search attorney..." 
                      className="h-10 border-0 border-b border-slate-200 rounded-none focus:ring-0 focus:border-slate-300 flex-shrink-0" 
                    />
                    <div className="flex-1 overflow-hidden">
                    <CommandList
                      className="max-h-[200px] overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
                      style={{
                        WebkitOverflowScrolling: "touch",
                        overscrollBehavior: "contain",    
                        touchAction: "pan-y"              
                      }}
>
                        <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                          No attorney found.
                        </CommandEmpty>
                        <CommandGroup>
                          {loadingAttorneys ? (
                            <CommandItem value="" disabled className="py-3 px-4">
                              <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2"></div>
                                Loading attorneys...
                              </div>
                            </CommandItem>
                          ) : (
                            attorneys.map((attorney) => (
                              <CommandItem
                                key={attorney.id}
                                value={attorney.name.toLowerCase()}
                                onSelect={() => {
                                  setSelectedAttorney(attorney.id)
                                  setAttorneyPopoverOpen(false)
                                }}
                                className="cursor-pointer py-3 px-4 hover:bg-slate-50 aria-selected:bg-slate-100"
                              >
                                <Check 
                                  className={cn(
                                    "mr-3 h-4 w-4 flex-shrink-0", 
                                    selectedAttorney === attorney.id ? "opacity-100 text-green-600" : "opacity-0"
                                  )} 
                                />
                                <span className="text-sm text-slate-700 truncate">{attorney.name}</span>
                              </CommandItem>
                            ))
                          )}
                        </CommandGroup>
                      </CommandList>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Select Firm</label>
              <Popover open={firmPopoverOpen} onOpenChange={setFirmPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between text-left font-normal h-10 px-3 py-2"
                  >
                    <span className="truncate">
                      {selectedFirm 
                        ? (() => {
                            const firm = firms.find(f => f.id === selectedFirm)
                            return firm ? `${firm.first_name} ${firm.last_name}` : "Choose a firm..."
                          })()
                        : "Choose a firm..."
                      }
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  container={modalContainerRef.current}
                  className="w-[var(--radix-popover-trigger-width)] min-w-[200px] max-w-[400px] p-0" 
                  align="start"
                  side="bottom"
                  sideOffset={4}
                  onWheel={(e) => e.stopPropagation()}
                >
                  <Command className="w-full h-full">
                    <CommandInput 
                      placeholder="Search firm..." 
                      className="h-10 border-0 border-b border-slate-200 rounded-none focus:ring-0 focus:border-slate-300 flex-shrink-0" 
                    />
                    <div className="flex-1 overflow-hidden">
                    <CommandList
                      className="max-h-[300px] overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
                      style={{
                        WebkitOverflowScrolling: "touch",
                        overscrollBehavior: "contain",
                        touchAction: "pan-y"
                      }}
                    >
                        <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                          No firm found.
                        </CommandEmpty>
                        {loadingFirms ? (
                          <CommandGroup>
                            <CommandItem value="" disabled className="py-3 px-4">
                              <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2"></div>
                                Loading firms...
                              </div>
                            </CommandItem>
                          </CommandGroup>
                        ) : (
                          // Group firms by law_firm
                          Object.entries(
                            firms.reduce((groups, firm) => {
                              const lawFirm = firm.law_firm || 'Other';
                              if (!groups[lawFirm]) {
                                groups[lawFirm] = [];
                              }
                              groups[lawFirm].push(firm);
                              return groups;
                            }, {} as Record<string, Firm[]>)
                          ).map(([lawFirm, firmList]) => (
                            <CommandGroup key={lawFirm} heading={lawFirm}>
                              {firmList.map((firm) => (
                                <CommandItem
                                  key={firm.id}
                                  value={`${firm.law_firm} ${firm.first_name} ${firm.last_name} ${firm.email}`.toLowerCase()}
                                  onSelect={() => {
                                    setSelectedFirm(firm.id)
                                    setFirmPopoverOpen(false)
                                  }}
                                  className="cursor-pointer py-3 px-4 hover:bg-slate-50 aria-selected:bg-slate-100"
                                >
                                  <Check 
                                    className={cn(
                                      "mr-3 h-4 w-4 flex-shrink-0", 
                                      selectedFirm === firm.id ? "opacity-100 text-green-600" : "opacity-0"
                                    )} 
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-700 truncate">
                                      {firm.first_name} {firm.last_name}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">
                                      {firm.email}
                                    </div>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          ))
                        )}
                      </CommandList>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          <DialogFooter className="flex gap-2 flex-col sm:flex-row">
            {currentAssignment && 
             assignmentType === currentAssignment.type && 
             onRemoveAssignment && (
              <Button 
                variant="outline" 
                onClick={handleRemoveAssignment}
                disabled={isRemoving}
                className="flex-1 sm:flex-initial border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
              >
                {isRemoving ? "Removing..." : "Remove Assignment"}
              </Button>
            )}
            <Button 
              onClick={handleAssignment}
              disabled={
                (assignmentType === "attorney" && !selectedAttorney) ||
                (assignmentType === "firm" && !selectedFirm)
              }
              className={
                assignmentType === "attorney" 
                  ? "bg-green-600 hover:bg-green-700 w-full sm:w-auto" 
                  : "bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              }
            >
              {assignmentType === "attorney" 
                ? (currentAssignment?.type === "attorney" && selectedAttorney === currentAssignment.id 
                    ? "Update Assignment" 
                    : "Assign to Attorney")
                : (currentAssignment?.type === "firm" && selectedFirm === currentAssignment.id 
                    ? "Update Referral" 
                    : "Refer to Firm")
              }
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
